// libraries
import React from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import * as QueryString from 'query-string';

// components
import GrantedPrivilegesProvider from '../entity/shared/GrantedPrivilegesProvider';
import { UnauthorisedPage } from '../shared/UnauthorisedPage';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { BrowseDataProduct } from './BrowseDataProduct';
import { LegacyBrowsePath } from './LegacyBrowsePath';
import { BrowseResults } from './BrowseResults';
import { PageRoutes } from '../../conf/Global';

// services
import { MANAGE_PLATFORM_PRIVILEGE_MAP, VIEW_PLATFORM_PRIVILEGE_MAP } from '../entity/shared/rbacConstantsService';
import { useGetBrowseResultsQuery } from '../../graphql/browse.generated';
import { useUserContext } from '../context/useUserContext';
import { useEntityRegistry } from '../useEntityRegistry';
import { scrollToTop } from '../shared/searchUtils';
import utilService from '../../utils/util.service';
import { BrowseCfg } from '../../conf';
import { EntityType, PlatformPrivileges } from '../../types.generated';

const DATA_PRODUCT = 'DATA_PRODUCT';

type BrowseResultsPageParams = {
    type: string;
};

const isAuthorized = (entityType: EntityType, platformPrivileges: PlatformPrivileges | null | undefined) => {
    const canView = platformPrivileges?.[VIEW_PLATFORM_PRIVILEGE_MAP[entityType] as keyof PlatformPrivileges] === true;
    const canManage =
        platformPrivileges?.[MANAGE_PLATFORM_PRIVILEGE_MAP[entityType] as keyof PlatformPrivileges] === true;

    return canView || canManage;
};

export const BrowseResultsPage = () => {
    const location = useLocation();
    const history = useHistory();
    const { type } = useParams<BrowseResultsPageParams>();

    const entityRegistry = useEntityRegistry();

    const rootPath = location.pathname;
    const params = QueryString.parse(location.search);
    const entityType = entityRegistry.getTypeFromPathName(type === 'dataproduct' ? 'dataProduct' : type);
    const pathComponents = rootPath.split('/').slice(3);

    // Decode special characters in the path
    const path = pathComponents.map((component) => utilService.decodeSpecialChars(component));

    const page: number = params.page && Number(params.page as string) > 0 ? Number(params.page as string) : 1;

    // RBAC: read platformPrivileges from UserContext (already fetched by UserContextProvider via getMe)
    const { platformPrivileges } = useUserContext();

    // RBAC: check if the user has the required privilege to access the browse results
    const hasAccess = isAuthorized(entityType, platformPrivileges);

    const { data, loading } = useGetBrowseResultsQuery({
        variables: {
            input: {
                type: entityType,
                path,
                start: (page - 1) * BrowseCfg.RESULTS_PER_PAGE,
                count: BrowseCfg.RESULTS_PER_PAGE,
                filters: null,
            },
        },
        // Skip until privileges are confirmed, and skip entirely if access is denied
        skip: !hasAccess || entityType === DATA_PRODUCT,
        errorPolicy: 'all',
    });

    const onChangePage = (newPage: number) => {
        scrollToTop();
        history.push({
            pathname: rootPath,
            search: `&page=${newPage}`,
        });
    };

    if (page < 0 || page === undefined || Number.isNaN(page)) {
        return <Redirect to={`${PageRoutes.BROWSE}`} />;
    }

    if (!hasAccess) {
        return <UnauthorisedPage />;
    }

    return (
        <GrantedPrivilegesProvider>
            <LegacyBrowsePath type={entityType} path={path} isBrowsable isLoading={loading} />
            {loading && <ComponentSpinner />}
            {entityType === DATA_PRODUCT ? (
                <BrowseDataProduct />
            ) : (
                data?.browse &&
                !loading && (
                    <BrowseResults
                        type={entityType}
                        rootPath={rootPath}
                        page={page}
                        pageSize={BrowseCfg.RESULTS_PER_PAGE}
                        groups={data.browse.groups}
                        entities={data.browse.entities}
                        totalResults={data.browse.total}
                        onChangePage={onChangePage}
                    />
                )
            )}
        </GrantedPrivilegesProvider>
    );
};
